/**
 * Template profiles: the few rows of a template company's ledger setup the app needs to build a
 * new company's ledger package itself (buildTemplateLedgerPackage), instead of uploading a full
 * export each time. Taken from the sales-cd9 demo environment's USMF export (2026-10-22):
 *
 * - Calendar 24hr, the USMF ledger (chart Shared, calendar Fiscal, structures, gain/loss accounts)
 * - A general-ledger set of journal names and every number sequence they or the GL references use
 * - Ledger parameters and USMF's main-account overrides
 * - The fiscal year's period names, status and module access (the build makes the as-of year and the next)
 *
 * Values are as exported, with record IDs and run-time state (DATETIME, INUSE) left out. Two USMF
 * sequences exported with a blank scope (GenJrl_02 for WFGenJrn, STATJNL) are scoped to USMF here.
 * To refresh: export the same entities from the template company and re-extract these rows.
 *
 * The Legends TRN template company 1100 has its own profile (templateProfile1100.ts); the
 * environment profile's template company picks one (templateProfileFor).
 */

import { LEGENDS_1100_ENTITIES } from './templateProfile1100.js';

export interface TemplateEntity {
  entityName: string;
  /** Internal entity name (manifest TargetEntity), when known */
  targetEntity?: string;
  columns: readonly string[];
  rows: ReadonlyArray<readonly string[]>;
}

export interface TemplateProfile {
  /** Template company ID, as the rows name it */
  company: string;
  title: string;
  /** Entities in import order (the fiscal periods are inserted after the ledger) */
  entities: readonly TemplateEntity[];
  /** One fiscal year of the template's periods: name and status, every module's access level "All" */
  fiscalCalendar: string;
  periods: ReadonlyArray<readonly [name: string, status: string]>;
  /** Fiscal years the package sets period status for, from the as-of year (they must exist) */
  fiscalYears: number;
  /**
   * Working times for the work calendar the workflows use for due dates: without open time in the
   * new company, a submitted workflow can't schedule its work items. Generated for `days` days from
   * the as-of date; every day open from `start` to `end` (template 247: 24 hours, 7 days).
   */
  workingTimes: { calendar: string; days: number; start: string; end: string; efficiency: string };
}

const USMF_ENTITIES: readonly TemplateEntity[] = [
  {
    entityName: 'Calendar',
    targetEntity: 'WorkCalendarEntity',
    columns: [
      'CALENDARID',
      'CALENDARNAME',
      'DEFAULTENDINGTIME',
      'DEFAULTSTARTINGTIME',
      'ISFRIDAYWORKINGDAY',
      'ISMONDAYWORKINGDAY',
      'ISSATURDAYWORKINGDAY',
      'ISSUNDAYWORKINGDAY',
      'ISTHURSDAYWORKINGDAY',
      'ISTUESDAYWORKINGDAY',
      'ISWEDNESDAYWORKINGDAY',
      'WORKHOURS',
    ],
    rows: [
      ['24hr', '24 hour', '24:00:00', '00:00:00', 'Yes', 'Yes', 'No', 'No', 'Yes', 'Yes', 'Yes', '24.000000'],
    ],
  },
  {
    entityName: 'Ledger',
    targetEntity: 'LedgerEntity',
    columns: [
      'LEGALENTITYID',
      'ACCOUNTINGCURRENCY',
      'ACCOUNTSTRUCTURENAME1',
      'ACCOUNTSTRUCTURENAME2',
      'ACCOUNTSTRUCTURENAME3',
      'BUDGETEXCHANGERATETYPE',
      'CHARTOFACCOUNTS',
      'DESCRIPTION',
      'EXCHANGERATETYPE',
      'FISCALCALENDAR',
      'ISBUDGETCONTROLENABLED',
      'MAINACCOUNTIDREALIZEDGAIN',
      'MAINACCOUNTIDREALIZEDLOSS',
      'MAINACCOUNTIDUNREALIZEDGAIN',
      'MAINACCOUNTIDUNREALIZEDLOSS',
      'NAME',
      'REPORTINGCURRENCY',
    ],
    rows: [
      [
        'USMF',
        'USD',
        'Manufacturing B/S',
        'Manufacturing P&L',
        'Statistical',
        'Budget',
        'Shared',
        'Contoso Entertainment System USA',
        'Default',
        'Fiscal',
        'Yes',
        '801500',
        '801300',
        '801600',
        '801400',
        'USMF',
        'USD',
      ],
    ],
  },
  {
    entityName: 'Number sequence code V2',
    targetEntity: 'NumberSequenceTableV2Entity',
    columns: [
      'NUMBERSEQUENCECODE',
      'SCOPETYPE',
      'SCOPEVALUE',
      'ANNOTATEDFORMAT',
      'CLEANUP',
      'COMPANY',
      'CONTINUOUS',
      'CYCLICAL',
      'FORMAT',
      'INTERVAL',
      'LARGEST',
      'MANUAL',
      'NAME',
      'NEXT',
      'OPERATINGUNITTYPES',
      'PREALLOCATION',
      'QUANTITYOFNUMBERS',
      'SKIPCOUNTING',
      'SMALLEST',
      'STOPPED',
      'TOAHIGHERNUMBER',
      'TOALOWERNUMBER',
    ],
    rows: [
      [
        'Alloc_01',
        'DataArea',
        'USMF',
        '-1\tALLC\n-2\t######',
        'Yes',
        'USMF',
        'No',
        'No',
        'ALLC######',
        '24.000000',
        '999999',
        'No',
        'Ledger allocations',
        '21',
        'None',
        'No',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'GenJrl_02',
        'DataArea',
        'USMF',
        '-1\tJE\n-2\t######',
        'Yes',
        'USMF',
        'No',
        'No',
        'JE######',
        '24.000000',
        '999999',
        'No',
        'WF General Journal',
        '2',
        'None',
        'No',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'GenJrn_01',
        'DataArea',
        'USMF',
        '-1\tGNJL\n-2\t######',
        'Yes',
        'USMF',
        'No',
        'No',
        'GNJL######',
        '24.000000',
        '999999',
        'No',
        'General Journal',
        '1047',
        'None',
        'No',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_113',
        'DataArea',
        'USMF',
        '-2\t#####',
        'Yes',
        'USMF',
        'Yes',
        'No',
        '#####',
        '24.000000',
        '99999',
        'No',
        'Gene_113',
        '25139',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_115',
        'DataArea',
        'USMF',
        '-2\t####',
        'Yes',
        'USMF',
        'No',
        'No',
        '####',
        '24.000000',
        '9999',
        'No',
        'Gene_115',
        '2001',
        'None',
        'No',
        '10',
        'No',
        '2000',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_116',
        'DataArea',
        'USMF',
        '-2\t######',
        'No',
        'USMF',
        'No',
        'No',
        '######',
        '.000000',
        '999999',
        'No',
        'Gene_116',
        '1',
        'None',
        'Yes',
        '25',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_119',
        'DataArea',
        'USMF',
        '-2\t######',
        'Yes',
        'USMF',
        'No',
        'No',
        '######',
        '24.000000',
        '999999',
        'No',
        'Gene_119',
        '1',
        'None',
        'No',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_120',
        'DataArea',
        'USMF',
        '-2\t######',
        'Yes',
        'USMF',
        'No',
        'No',
        '######',
        '24.000000',
        '999999',
        'No',
        'Gene_120',
        '33303',
        'None',
        'No',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_121',
        'DataArea',
        'USMF',
        '-2\t#####',
        'No',
        'USMF',
        'No',
        'No',
        '#####',
        '.000000',
        '99999',
        'No',
        'Gene_121',
        '6471',
        'None',
        'Yes',
        '25',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_122',
        'DataArea',
        'USMF',
        '-2\t#####',
        'No',
        'USMF',
        'No',
        'No',
        '#####',
        '.000000',
        '99999',
        'Yes',
        'Gene_122',
        '11',
        'None',
        'Yes',
        '25',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_123',
        'DataArea',
        'USMF',
        '-2\t#####',
        'No',
        'USMF',
        'No',
        'No',
        '#####',
        '.000000',
        '99999',
        'Yes',
        'Gene_123',
        '35',
        'None',
        'Yes',
        '25',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_124',
        'DataArea',
        'USMF',
        '-2\t#####',
        'No',
        'USMF',
        'No',
        'No',
        '#####',
        '.000000',
        '99999',
        'No',
        'Gene_124',
        '1',
        'None',
        'Yes',
        '25',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'IntJrn_01',
        'DataArea',
        'USMF',
        '-1\tICJL\n-2\t######',
        'Yes',
        'USMF',
        'No',
        'No',
        'ICJL######',
        '24.000000',
        '999999',
        'No',
        'Intercompany Journal',
        '2',
        'None',
        'No',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'PerJrn_01',
        'DataArea',
        'USMF',
        '-1\tPERJ\n-2\t######',
        'Yes',
        'USMF',
        'No',
        'No',
        'PERJ######',
        '24.000000',
        '999999',
        'No',
        'Periodic Journal',
        '1',
        'None',
        'No',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'STATJNL',
        'DataArea',
        'USMF',
        '-1\tSTAT\n-2\t####',
        'Yes',
        'USMF',
        'No',
        'No',
        'STAT####',
        '24.000000',
        '9999',
        'No',
        'Stat Journal',
        '9',
        'None',
        'No',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'WriteOff01',
        'DataArea',
        'USMF',
        '-1\tWOJL\n-2\t######',
        'Yes',
        'USMF',
        'No',
        'No',
        'WOJL######',
        '24.000000',
        '999999',
        'No',
        'Write-off Journal',
        '5',
        'None',
        'No',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
    ],
  },
  {
    entityName: 'Number sequence references V2',
    targetEntity: 'NumberSequencesReferenceV2Entity',
    columns: ['SCOPETYPE', 'SCOPEVALUE', 'DATATYPENAME', 'NUMBERSEQUENCECODE', 'REUSENUMBERS'],
    rows: [
      ['DataArea', 'USMF', 'GeneralJournalEntryJournalNumber', 'Gene_120', 'No'],
      ['DataArea', 'USMF', 'LedgerAllocationBasisID', 'Gene_123', 'No'],
      ['DataArea', 'USMF', 'LedgerAllocationRuleID', 'Gene_122', 'No'],
      ['DataArea', 'USMF', 'LedgerEliminationRuleId', 'Gene_124', 'No'],
      ['DataArea', 'USMF', 'LedgerExchAdjVoucher', 'Gene_115', 'No'],
      ['DataArea', 'USMF', 'LedgerJournalId', 'Gene_113', 'No'],
      ['DataArea', 'USMF', 'LedgerJournalizeNum', 'Gene_116', 'No'],
      ['DataArea', 'USMF', 'LedgerTempVoucher', 'Gene_119', 'No'],
      ['DataArea', 'USMF', 'SubledgerJournalNum', 'Gene_121', 'No'],
    ],
  },
  {
    entityName: 'Journal names',
    targetEntity: 'LedgerJournalNameEntity',
    columns: [
      'NAME',
      'AMOUNTSINCLUDESALESTAX',
      'DELETELINESAFTERPOSTING',
      'DESCRIPTION',
      'DETAILLEVEL',
      'FEESPOSTING',
      'HIDESALESTAXFIELDSINJOURNALENTRYFORMS',
      'ISAPPROVALACTIVE',
      'ISFIXEDEXCHANGERATE',
      'ISFIXEDOFFSETACCOUNT',
      'ISWORKFLOWAPPROVALACTIVE',
      'NEWVOUCHER',
      'OFFSETACCOUNTDISPLAYVALUE',
      'OFFSETACCOUNTTYPE',
      'OVERRIDESALESTAX',
      'POSTINGLAYER',
      'POSTINGLINELIMIT',
      'TYPE',
      'VOUCHERALLOCATIONATPOSTING',
      'VOUCHERSERIESCODE',
      'VOUCHERSERIESCOMPANYID',
      'WORKFLOWID',
    ],
    rows: [
      [
        'GenJrn',
        'No',
        'No',
        'General Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'No',
        'BalanceSheet',
        '',
        'Ledger',
        'No',
        'Current',
        '0',
        'Daily',
        'No',
        'GenJrn_01',
        'USMF',
        '',
      ],
      [
        'WFGenJrn',
        'No',
        'No',
        'General Journal - Workflow',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'BalanceSheet',
        '',
        'Ledger',
        'No',
        'Current',
        '0',
        'Daily',
        'No',
        'GenJrl_02',
        '',
        '000131',
      ],
      [
        'PerJrn',
        'No',
        'No',
        'Periodic Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'No',
        'BalanceSheet',
        '',
        'Ledger',
        'No',
        'Current',
        '0',
        'Periodic',
        'No',
        'PerJrn_01',
        'USMF',
        '',
      ],
      [
        'Allocation',
        'No',
        'No',
        'Ledger allocations',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'No',
        'BalanceSheet',
        '',
        'Ledger',
        'No',
        'Current',
        '0',
        'Allocation',
        'No',
        'Alloc_01',
        'USMF',
        '',
      ],
      [
        'IntJrn',
        'No',
        'No',
        'Intercompany Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'No',
        'BalanceSheet',
        '',
        'Ledger',
        'No',
        'Current',
        '0',
        'Daily',
        'No',
        'IntJrn_01',
        'USMF',
        '',
      ],
      [
        'WriteOff',
        'No',
        'No',
        'Write-off Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'No',
        'BalanceSheet',
        '',
        'Ledger',
        'No',
        'Current',
        '0',
        'Daily',
        'No',
        'WriteOff01',
        'USMF',
        '',
      ],
      [
        'STAT',
        'No',
        'No',
        'Statistical',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'Yes',
        'No',
        'BalanceSheet',
        'XOFFSET',
        'Ledger',
        'No',
        'Current',
        '0',
        'Daily',
        'No',
        'STATJNL',
        '',
        '',
      ],
      [
        'Rev Rec',
        'No',
        'No',
        'Revenue recognition',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'No',
        'BalanceSheet',
        '',
        'Ledger',
        'No',
        'Current',
        '0',
        'RevenueRecognition',
        'No',
        'Gene_116',
        'USMF',
        '',
      ],
    ],
  },
  {
    entityName: 'Ledger parameters',
    targetEntity: 'LedgerParametersEntity',
    columns: [
      'ALLOWMULTIPLETRANSACTIONSWITHINONEVOUCHER',
      'CREATECLOSINGTRANSACTIONSDURINGFISCALYEARCLOSE',
      'CREATEREVERSALSASCORRECTIONS',
      'DELETEPREVIOUSCLOSINGTRANSACTIONSDURINGFISCALYEARCLOSE',
      'DIMENSIONVALUESUSEDFORSUMMARYACCOUNTOPTION',
      'DONOTUSEERRORACCOUNT',
      'DUPLICATEVOUCHEROPTION',
      'ENABLELEDGERSETTLEMENT',
      'ENABLEREVERSECHARGE',
      'MAXIMUMPENNYDIFFERENCEINACCOUNTINGCURRENCY',
      'MAXIMUMPENNYDIFFERENCEINREPORTINGCURRENCY',
      'ORDERJOURNALIZINGREPORTBYCREATEDDATEANDTIME',
      'PROCESSSOURCEDOCUMENTLINESINPARALLEL',
      'PSNLEDGERINTERESTDISTRIBUTIONENABLE',
      'PSNLEDGERINTERESTDISTRIBUTIONFUNDINGSOURCERULES',
      'PSNLEDGERINTERESTDISTRIBUTIONPOSTALE',
      'PSNPRIORYEARCORRECTION',
      'REQUIRECONTINUOUSNUMBERSEQUENCES',
      'REQUIREJOURNALIZINGONFISCALPERIODCLOSE',
      'REVRECDISABLECONTRACTTERMS',
      'REVRECENABLEREALLOCATION',
      'REVRECHEADERDISCOUNTS',
      'REVRECJOURNALNAMEID',
      'REVRECPOSTTOACCOUNTSRECEIVABLE',
      'SETFISCALYEARTOCLOSEDDURINGFISCALYEARCLOSE',
      'SKIPBUDGETTRANSACTIONSFORFISCALYEARCLOSE',
      'STARTJOURNALIZEREPORTONPAGEONE',
      'USEBUDGETAPPROPRIATION',
      'USEBUDGETRESERVATIONPROCESS',
      'USEENCUMBRANCEPROCESS',
      'USEFUNDDIMENSIONTOCARRYFORWARDPURCHASEORDERS',
      'USEPOSTINGDEFINITIONS',
      'USEPREENCUMBRANCEPROCESS',
      'VOUCHERNUMBERREQUIREDFORFISCALYEARCLOSE',
      'WITHHOLDINGTAXMINIMUMINVOICEAMOUNT',
    ],
    rows: [
      [
        'No',
        'No',
        'No',
        'Yes',
        'AccountingDistributions',
        'No',
        'NoDuplicate',
        'Yes',
        'No',
        '.050000',
        '.050000',
        'No',
        'No',
        'No',
        'DefineFundingRules',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'No',
        'Rev Rec',
        'Yes',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        '.000000',
      ],
    ],
  },
  {
    entityName: 'Main account legal entity overrides',
    targetEntity: 'MainAccountLegalEntityOverrideEntity',
    columns: [
      'MAINACCOUNTID',
      'CHARTOFACCOUNTS',
      'LEGALENTITYID',
      'ACTIVEFROM',
      'ACTIVETO',
      'AUTOALLOCATE',
      'DEFAULTDIMENSIONDISPLAYVALUE',
      'DONOTALLOWMANUALENTRY',
      'FIXEDDIMENSIONSDISPLAYVALUE',
      'ISEXEMPT',
      'ISSUSPENDED',
      'SALESTAXDIRECTION',
      'VALIDATESALESTAX',
    ],
    rows: [
      [
        '110110',
        'Shared',
        'USMF',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '',
        'No',
        '',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '150100',
        'Shared',
        'USMF',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '001---',
        'No',
        'Fixed value-Not fixed-Not fixed-',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '510360',
        'Shared',
        'USMF',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '001---',
        'No',
        'Fixed value-Not fixed-Not fixed-',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '600500',
        'Shared',
        'USMF',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '001---',
        'No',
        'Fixed value-Not fixed-Not fixed-',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '618160',
        'Shared',
        'USMF',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '001---',
        'No',
        'Fixed value-Not fixed-Not fixed-',
        'No',
        'No',
        'None',
        'Optional',
      ],
    ],
  },
];

export const TEMPLATE_PROFILES = {
  USMF: {
    company: 'USMF',
    title: 'USMF (sales-cd9 demo environment)',
    entities: USMF_ENTITIES,
    fiscalCalendar: 'Fiscal',
    periods: [
      ['Opening', 'Closed'],
      ...Array.from({ length: 12 }, (_, m) => [`Period ${m + 1}`, 'Open'] as const),
      ['Closing', 'OnHold'],
    ],
    fiscalYears: 2,
    // USMF's 24hr calendar (export 2026-10-23) is open 00:00–24:00 on weekdays; template 247 opens
    // every day, so a journal submitted at the weekend still schedules.
    workingTimes: {
      calendar: '24hr',
      days: 30,
      start: '00:00:00',
      end: '24:00:00',
      efficiency: '100.000000',
    },
  },
  '1100': {
    company: '1100',
    title: '1100 Legends Hospitality, LLC (Legends TRN sandbox)',
    entities: [...USMF_ENTITIES.filter((e) => e.entityName === 'Calendar'), ...LEGENDS_1100_ENTITIES],
    fiscalCalendar: 'Fiscal',
    // 1100's Fiscal calendar: Period 0 (opening), Periods 1–12, Period 13 (closing). The export has
    // only Periods 9–10 open in 2026; a new venue opens the year, like the USMF profile.
    periods: [
      ['Period 0', 'Closed'],
      ...Array.from({ length: 12 }, (_, m) => [`Period ${m + 1}`, 'Open'] as const),
      ['Period 13', 'OnHold'],
    ],
    // The TRN export (2026-10-22) has fiscal years 2024–2026 only: a status row for a year the
    // calendar lacks fails the import. VERIFY the next year exists before raising this to 2.
    fiscalYears: 1,
    workingTimes: {
      calendar: '24hr',
      days: 30,
      start: '00:00:00',
      end: '24:00:00',
      efficiency: '100.000000',
    },
  },
} as const satisfies Record<string, TemplateProfile>;

export type TemplateProfileId = keyof typeof TEMPLATE_PROFILES;

/** The stored profile for a template company (an environment profile's templateCompany), or USMF. */
export function templateProfileFor(company: string): TemplateProfileId {
  return Object.hasOwn(TEMPLATE_PROFILES, company) ? (company as TemplateProfileId) : 'USMF';
}
